import { useEffect, useState } from 'react';
import './BussinessPage.css';

function BusinessImage({ section }) {
  const images = section.images || [section.image];
  const [activeIndex, setActiveIndex] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [autoPlayStopped, setAutoPlayStopped] = useState(false);
  const hasSlider = images.length > 1;
  const isFirstSlide = activeIndex === 0;
  const isLastSlide = activeIndex === images.length - 1;

  useEffect(() => {
    if (!hasSlider || autoPlayStopped) {
      return undefined;
    }

    const timer = setInterval(() => {
      setActiveIndex((currentIndex) => (currentIndex + 1) % images.length);
    }, 3000);

    return () => clearInterval(timer);
  }, [autoPlayStopped, hasSlider, images.length]);

  useEffect(() => {
    if (!isExpanded) {
      return undefined;
    }

    const scrollY = window.scrollY;
    const previousBodyOverflow = document.body.style.overflow;
    const previousBodyPosition = document.body.style.position;
    const previousBodyTop = document.body.style.top;
    const previousBodyWidth = document.body.style.width;

    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';

    const handleModalKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsExpanded(false);
      }

      if (event.key === 'ArrowLeft' && !isFirstSlide) {
        setAutoPlayStopped(true);
        setActiveIndex((currentIndex) => Math.max(currentIndex - 1, 0));
      }

      if (event.key === 'ArrowRight' && !isLastSlide) {
        setAutoPlayStopped(true);
        setActiveIndex((currentIndex) => Math.min(currentIndex + 1, images.length - 1));
      }
    };

    document.addEventListener('keydown', handleModalKeyDown);
    return () => {
      document.removeEventListener('keydown', handleModalKeyDown);
      document.body.style.overflow = previousBodyOverflow;
      document.body.style.position = previousBodyPosition;
      document.body.style.top = previousBodyTop;
      document.body.style.width = previousBodyWidth;
      window.scrollTo(0, scrollY);
    };
  }, [images.length, isExpanded, isFirstSlide, isLastSlide]);

  const showPrevious = (stopAutoPlay = false) => {
    if (stopAutoPlay) {
      setAutoPlayStopped(true);
    }

    setActiveIndex((currentIndex) => Math.max(currentIndex - 1, 0));
  };

  const showNext = (stopAutoPlay = false) => {
    if (stopAutoPlay) {
      setAutoPlayStopped(true);
    }

    setActiveIndex((currentIndex) => Math.min(currentIndex + 1, images.length - 1));
  };

  return (
    <>
      <div
        className={`business-image${hasSlider ? ' business-image-slider' : ''}`}
        onClick={() => setIsExpanded(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setIsExpanded(true);
          }
        }}
      >
        <div
          className="business-slider-track"
          style={{ transform: `translateX(-${activeIndex * 100}%)` }}
        >
          {images.map((image) => (
            <img src={image} alt={section.title} key={image} />
          ))}
        </div>

        {hasSlider && (
          <>
            {!isFirstSlide && (
              <button
                className="business-slider-button business-slider-button-prev"
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  showPrevious(true);
                }}
                aria-label="前の画像"
              >
                ‹
              </button>
            )}
            {!isLastSlide && (
              <button
                className="business-slider-button business-slider-button-next"
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  showNext(true);
                }}
                aria-label="次の画像"
              >
                ›
              </button>
            )}
          </>
        )}
      </div>

      {isExpanded && (
        <div
          className="business-image-modal"
          onClick={() => setIsExpanded(false)}
          role="presentation"
        >
          <button
            className="business-image-modal-close"
            type="button"
            onClick={() => setIsExpanded(false)}
            aria-label="画像を閉じる"
          >
            ×
          </button>
          {hasSlider && !isFirstSlide && (
            <button
              className="business-image-modal-arrow business-image-modal-arrow-prev"
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                showPrevious(true);
              }}
              aria-label="前の拡大画像"
            >
              ‹
            </button>
          )}
          <img
            className="business-image-modal-img"
            src={images[activeIndex]}
            alt={section.title}
            onClick={(event) => event.stopPropagation()}
          />
          {hasSlider && !isLastSlide && (
            <button
              className="business-image-modal-arrow business-image-modal-arrow-next"
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                showNext(true);
              }}
              aria-label="次の拡大画像"
            >
              ›
            </button>
          )}
        </div>
      )}
    </>
  );
}

function BussinessPage(){
  const sections = [
    {
      title: '型整機関連装置の整備',
      image: '/images/business-04.JPG',
      images: [
        '/images/business-04.JPG',
        '/images/business-04-2.JPG',
        '/images/business-04-3.JPG',
      ],
      lead: [
        '成形精度は、製品の見た目と品質を左右します。',
      ],
      points: [
        '成形型の調整',
        '駆動部の摩耗修正',
        'ベルト・ガイド交換',
        '成形寸法の再調整',
      ],
      note: '「元の精度に戻す」整備を得意としています。',
    },
    {
      title: 'コンベア設備の設計・製造・整備・改修',
      image: '/images/business-02.JPG',
      images: [
        '/images/business-02.JPG',
        '/images/business-02-2.JPG',
        '/images/business-02-3.JPG',
      ],
      lead: [
        '食品工場の要となるコンベア設備。',
        '搬送不良や停止トラブルは、生産ライン全体の',
        '大きなロスにつながります。',
      ],
      points: [
        '設備に応じたオリジナル仕様の提案',
        'ベルト交換・蛇行修正',
        '駆動部・モーター点検',
        'ステンレスフレーム補修・改造',
        'レイアウト変更・高さ変更等の作業',
      ],
      note: '既存設備を活かした改善提案も可能です。',
    },
    {
      title: '食品製造機械の設計・製造・整備・メンテナンス',
      image: '/images/business-01.jpg',
      lead: [
        'かまぼこをはじめとした水産加工食品・食品工場向け機械の',
        '設計・製造・点検・整備・修理・調整を行っています。',
        '長年使用されてきた機械は、わずかなズレや摩耗が',
        '製品品質や歩留まりに大きく影響します。',
      ],
      points: [
        '消耗部品の確認・交換',
        '成形部の調整',
        '異音・振動の原因特定',
        '食品機械特有の衛生面を考慮した整備',
      ],
      note: '機械に合わせた確実な整備を行います。',
    },
    {
      title: 'ボイル・加熱設備の点検整備',
      image: '/images/business-03.JPG',
      lead: [
        'ボイル機・加熱ラインは、',
        '温度管理・安全性・安定稼働が重要です。',
      ],
      points: [
        '加熱ムラ',
        '温度が安定しない',
        '蒸気漏れ',
        'バルブの固着',
      ],
      note: '定期点検・部品交換・調整整備を行います。',
    },
    {
      title: '熱交換機・配管設備・その他機械整備',
      image: '/images/business-05.jpg',
      lead: [
        '食品工場には、さまざまな設備が存在します。',
        'メーカー対応が終了した機械や、',
        '図面が残っていない設備でも対応可能です。',
      ],
      points: [
        '熱交換機',
        'ポンプユニット',
        '配管設備',
        '圧力タンク',
        '古くなった専用機',
      ],
      note: ['「どこに頼めばいいか分からない」', 'そんな時こそ、ぜひご相談ください。'],
    },
  ];

  return (
    <div className="BussinessPage">
      <div className="business-hero">
        <span className="business-hero-title">事業紹介</span>
      </div>

      <div className="business-sections">
        {sections.map((section) => (
          <div className="business-section" key={section.title}>
            <BusinessImage section={section} />
            <div className="business-text">
              <h3>{section.title}</h3>
              <div className="business-lead">
                {section.lead.map((line) => (
                  <p key={line}>{line}</p>
                ))}
              </div>
              <ul>
                {section.points.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
              <p className="business-note">
                {Array.isArray(section.note)
                  ? section.note.map((line, lineIndex) => (
                      <span key={`${section.title}-note-${lineIndex}`}>
                        {line}
                        {lineIndex < section.note.length - 1 && <br />}
                      </span>
                    ))
                  : section.note}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BussinessPage;
